import { Injectable } from '@angular/core';
import { DTO, Types } from 'shared-lib';
import { FileService } from '../../../service';
import { WebsocketService } from '../../../service/websocket.service';
import { AuthService } from '../../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  contact?: DTO.ContactDTO;

  constructor(
    private webSocket: WebsocketService,
    private fileService: FileService,
    private authService: AuthService
  ) {}

  connectWS(contact: DTO.ContactDTO, callback: () => void) {
    this.contact = contact;
    this.webSocket.connect(callback);
  }

  disconnectWs() {
    this.webSocket?.disconnect();
  }

  listenForMarkAsRead(callback: (data: DTO.ChatEventDTO<undefined>) => void) {
    this.webSocket.addListener(Types.EChatEvents.MARK_AS_READ, callback);
  }

  listenMessages(
    callback: (data: DTO.ChatEventDTO<DTO.MessagesDTO[]>) => void
  ) {
    this.webSocket.addListener(Types.EChatEvents.LOAD_MESSAGES, callback);
  }

  listenUsersOnline(
    callback: (data: DTO.ChatEventDTO<DTO.ContactDTO[]>) => void
  ) {
    this.webSocket.addListener(Types.EChatEvents.ACTIVE_USERS, callback);
  }

  listenNewMessage(
    callback: (data: DTO.ChatEventDTO<DTO.MessagesDTO>) => void
  ) {
    this.webSocket.addListener(Types.EChatEvents.NEW_MESSAGE, callback);
  }

  listenNotificationNewContact(
    callback: (data: DTO.ChatEventDTO<DTO.ContactDTO[]>) => void
  ) {
    this.webSocket.addListener(
      Types.EChatEvents.NOTIFICATION_NEW_CONTACT,
      callback
    );
  }

  listenNotificationNewMessage(
    callback: (data: DTO.ChatEventDTO<DTO.MessagesDTO>) => void
  ) {
    this.webSocket.addListener(
      Types.EChatEvents.NOTIFICATION_NEW_MESSAGE,
      callback
    );
  }

  listenTyping(callback: (data: DTO.ChatEventDTO<DTO.ContactDTO>) => void) {
    this.webSocket.addListener(Types.EChatEvents.TYPING, callback);
  }

  sendRead() {
    const id = this.authService.getUser()?.sub;
    if (!this.contact || !id) {
      return;
    }
    const chatEventDTO = new DTO.ChatEventDTO<undefined>();
    chatEventDTO.meta = {
      receiver: this.contact,
      sender: { id },
    };
    this.webSocket.sendEvent(Types.EChatEvents.MARK_AS_READ, chatEventDTO);
  }

  sendTyping() {
    const id = this.authService.getUser()?.sub;
    if (!this.contact || !id) {
      return;
    }
    const chatEventDTO = new DTO.ChatEventDTO<undefined>();
    chatEventDTO.meta = {
      receiver: this.contact,
      sender: { id },
    };
    this.webSocket.sendEvent(Types.EChatEvents.TYPING, chatEventDTO);
  }

  async getUsertAvatar(): Promise<string> {
    const avatar = this.authService.getUserData()?.avatar;
    let base64 = await this.fileService.loadFile(avatar?.id ?? 0);
    return base64 || '/images/user_image.png';
  }

  loadMessages() {
    const id = this.authService.getUser()?.sub;
    if (!this.contact || !id) {
      return;
    }
    const chatEventDTO = new DTO.ChatEventDTO<undefined>();
    chatEventDTO.meta = {
      receiver: this.contact,
      sender: { id },
    };
    this.webSocket.sendEvent(Types.EChatEvents.ENTER_ROOM_CHAT, chatEventDTO);
    this.webSocket.sendEvent(Types.EChatEvents.LOAD_MESSAGES, chatEventDTO);
  }

  sendMessage(message: DTO.MessagesDTO) {
    const id = this.authService.getUser()?.sub;
    if (!this.contact || !id) {
      return;
    }
    const chatEventDTO = new DTO.ChatEventDTO<DTO.MessagesDTO>();
    chatEventDTO.meta = {
      receiver: this.contact,
      sender: { id },
    };
    chatEventDTO.payload = message;
    this.webSocket.sendEvent(Types.EChatEvents.NEW_MESSAGE, chatEventDTO);
  }
}
